import { useState, lazy, Suspense, useEffect, useCallback, useMemo } from 'react';
import { PagePreloader } from './utils/pagePreloader';
import { Header, Footer } from './components/Layout';
import { Home } from './pages/Home';
import { ColorSchemeProvider } from './contexts/ColorSchemeContext';
import { ThemeProvider } from './contexts/ThemeContext';
import { CurrencyProvider } from './contexts/CurrencyContext';
import { NotificationProvider } from './contexts/NotificationContext';
import { AuthProvider } from './contexts/AuthContext';
import { ThemeSelectorModal } from './components/Theme';
import { useTranslation } from 'react-i18next';
import { AIHubProvider } from './contexts/AIHubContext';
import { EmailHubProvider } from './contexts/EmailHubContext';
// Note: useUserProfile can be imported if admin features are needed
// import { useUserProfile } from './hooks/useUserProfile';
import CookieBanner from './components/Privacy/CookieBanner';
import { useTheme } from './contexts/ThemeContext';
import { DynamicOpenGraph, SeoVerticalBody } from './components/SEO';
import { HreflangTags } from './components/SEO/MultilingualSEO';
import { BreadcrumbsJSONLD } from './components/SEO/BreadcrumbsJSONLD';
import { LanguageSuggestionBanner } from './components/LanguageSuggestionBanner';
import { navigationLogger as logger } from './lib/logger';
import { resolveSupportedLanguage } from './i18n';
import { HelpChatFabGate } from './components/support/HelpChatFabGate';
import { ONYX_NAVIGATE_EVENT } from './lib/onyxChatNavigation';

const LazyParticles = lazy(() =>
  import('./components/effects/ProfessionalParticles').then((m) => ({
    default: m.ProfessionalParticles,
  }))
);
// Lazy loaded pages
const Pricing = lazy(() => import('./pages/Pricing').then(m => ({ default: m.Pricing })));

const Contact = lazy(() => import('./pages/Contact').then(m => ({ default: m.Contact })));
const Compare = lazy(() => import('./pages/Compare').then(m => ({ default: m.Compare })));
const CompareLanding = lazy(() => import('./pages/LandingArticle').then(m => ({ default: m.CompareLanding })));
const UseCaseLanding = lazy(() => import('./pages/LandingArticle').then(m => ({ default: m.UseCaseLanding })));
const ReplaceLanding = lazy(() => import('./pages/LandingArticle').then(m => ({ default: m.ReplaceLanding })));
const About = lazy(() => import('./pages/About').then(m => ({ default: m.About })));
const Blog = lazy(() => import('./pages/Blog').then(m => ({ default: m.Blog })));
const BlogPost = lazy(() => import('./pages/BlogPost').then(m => ({ default: m.BlogPost })));
const Press = lazy(() => import('./pages/Press').then(m => ({ default: m.Press })));
const Team = lazy(() => import('./pages/Team').then(m => ({ default: m.Team })));
const Music = lazy(() => import('./pages/Music').then(m => ({ default: m.Music })));
const Careers = lazy(() => import('./pages/Careers').then(m => ({ default: m.Careers })));
const AIPolicyPage = lazy(() => import('./pages/AIPolicyPage').then(m => ({ default: m.AIPolicyPage })));
const SupportCenter = lazy(() => import('./pages/SupportCenter').then(m => ({ default: m.SupportCenter })));
const SupportTicketForm = lazy(() => import('./pages/SupportTicketForm'));
const Success = lazy(() => import('./pages/Success').then(m => ({ default: m.Success })));
const CGV = lazy(() => import('./pages/CGV').then(m => ({ default: m.CGV })));
const RGPD = lazy(() => import('./pages/RGPD').then(m => ({ default: m.RGPD })));
const CheckoutPage = lazy(() => import('./pages/CheckoutPage').then(m => ({ default: m.CheckoutPage })));
const ResetPassword = lazy(() => import('./pages/ResetPassword').then(m => ({ default: m.ResetPassword })));

// Admin components
const AdminProtectedRoute = lazy(() => import('./components/admin/AdminProtectedRoute'));
const UnifiedAdminDashboard = lazy(() => import('./pages/admin/UnifiedAdminDashboard'));
const TicketDetailView = lazy(() => import('./pages/admin/TicketDetailView').then(m => ({ default: m.TicketDetailView })));

function PageFallback() {
  const [showFallback, setShowFallback] = useState(false);
  const [progress, setProgress] = useState(0);

  useEffect(() => {
    // Délai de 150ms avant d'afficher le fallback (plus rapide pour premium UX)
    const timer = setTimeout(() => {
      setShowFallback(true);
    }, 150);

    return () => clearTimeout(timer);
  }, []);

  useEffect(() => {
    if (!showFallback) return;

    // Animation de progression premium
    const progressTimer = setInterval(() => {
      setProgress(prev => {
        if (prev >= 90) return prev; // Ralentir vers la fin
        return prev + Math.random() * 15;
      });
    }, 100);

    return () => clearInterval(progressTimer);
  }, [showFallback]);

  if (!showFallback) {
    // Pendant les 150ms : div invisible pour maintenir la structure
    return <div className="min-h-[50vh] opacity-0"></div>;
  }

  return (
    <div className="min-h-[50vh] relative overflow-hidden">
      {/* Overlay premium avec gradient */}
      <div className="absolute inset-0 bg-gradient-to-br from-gray-50 via-white to-gray-50 dark:from-gray-900 dark:via-gray-800 dark:to-gray-900" />
      
      {/* Progress bar premium en haut */}
      <div className="absolute top-0 left-0 right-0 h-0.5 bg-gray-200 dark:bg-gray-700">
        <div 
          className="h-full bg-gradient-to-r from-yellow-400 to-yellow-600 transition-all duration-300 ease-out"
          style={{ 
            width: `${progress}%`,
            boxShadow: '0 0 10px rgba(251, 191, 36, 0.5)'
          }}
        />
      </div>

      {/* Contenu centré avec animation premium */}
      <div className="flex items-center justify-center min-h-[50vh] premium-fade-in">
        <div className="flex flex-col items-center space-y-6">
          {/* Spinner premium avec double cercle */}
          <div className="relative">
            <div className="w-10 h-10 border-2 border-gray-200 dark:border-gray-600 rounded-full"></div>
            <div className="absolute top-0 left-0 w-10 h-10 border-2 border-yellow-500 rounded-full border-t-transparent animate-spin"></div>
            <div className="absolute top-1 left-1 w-8 h-8 border border-yellow-300 rounded-full border-t-transparent animate-spin" style={{ animationDirection: 'reverse', animationDuration: '1.5s' }}></div>
          </div>
          
          {/* Message avec animation subtile */}
          <div className="text-center space-y-2">
            <p className="text-sm text-gray-600 dark:text-gray-400 font-medium premium-pulse">
              Chargement...
            </p>
            <div className="flex space-x-1">
              <div className="w-1 h-1 bg-yellow-500 rounded-full animate-bounce" style={{ animationDelay: '0ms' }}></div>
              <div className="w-1 h-1 bg-yellow-500 rounded-full animate-bounce" style={{ animationDelay: '150ms' }}></div>
              <div className="w-1 h-1 bg-yellow-500 rounded-full animate-bounce" style={{ animationDelay: '300ms' }}></div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

const getPageFromPath = (path: string): string => {
  // Admin routes - check specific routes first
  if (path.includes('/_console/ticket/')) return 'admin-ticket-detail';
  if (path.includes('/_console') || path.includes('/admin')) return 'admin';
  if (path.includes('/auth/reset-password')) return 'reset-password';
  if (path.includes('/pricing')) return 'pricing';
  if (path.includes('/contact')) return 'contact';
  // Nested landings before hub /compare/
  if (path.match(/\/compare\/[^/]+\/?$/) && !path.match(/\/compare\/?$/)) return 'compare-landing';
  if (path.match(/\/use-case\/[^/]+\/?$/)) return 'use-case';
  if (path.match(/\/replace\/[^/]+\/?$/)) return 'replace-landing';
  if (path.includes('/compare')) return 'compare';
  if (path.includes('/about')) return 'about';
  if (path.includes('/blog/') && path.split('/').filter(Boolean).length > 2 && !path.endsWith('/blog/')) return 'blog-post'; // Blog post individual
  if (path.includes('/blog')) return 'blog';
  // Press *page* only — never treat /press/kit/*.pdf (or missing-PDF SPA fallback) as the Press route
  if (/(?:^|\/)press(?:\/|$)/.test(path) && !path.includes('/press/kit')) return 'press';
  if (path.includes('/team')) return 'team';
  if (path.includes('/music')) return 'music';
  if (path.includes('/careers')) return 'careers';
  if (path.includes('/ai-policy')) return 'ai-policy';
  if (path.includes('/help')) return 'help';
  if (path.includes('/request')) return 'support-request';
  if (path.includes('/success')) return 'success';
  if (path.includes('/cgv')) return 'cgv';
  if (path.includes('/rgpd')) return 'rgpd';
  if (path.includes('/gdpr')) return 'rgpd';
  if (path.includes('/checkout')) return 'checkout';
  
  return 'home';
};

function AppContent() {
  const { surface } = useTheme();
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [currentPage, setCurrentPage] = useState(() => getPageFromPath(window.location.pathname));
  const { ready, i18n } = useTranslation(['common']);
  const [detectedCountry, setDetectedCountry] = useState<string | undefined>();
  const [particlesReady, setParticlesReady] = useState(false);
  // Note: useUserProfile is available for future admin features if needed
  // const { profile } = useUserProfile();

  // Defer Three.js particles until after first paint (CWV)
  useEffect(() => {
    const enable = () => setParticlesReady(true);
    if (typeof window !== 'undefined' && 'requestIdleCallback' in window) {
      const id = (window as Window & { requestIdleCallback: (cb: () => void, opts?: { timeout: number }) => number })
        .requestIdleCallback(enable, { timeout: 3500 });
      return () => {
        const w = window as Window & { cancelIdleCallback?: (id: number) => void };
        w.cancelIdleCallback?.(id);
      };
    }
    const t = window.setTimeout(enable, 2000);
    return () => clearTimeout(t);
  }, []);
  
  // 🚨 GARDE-FOU : Vérifier que la langue correspond à l'URL (casse canonique)
  useEffect(() => {
    const raw =
      window.location.pathname.match(/^\/([a-zA-Z]{2}(?:-[a-zA-Z]{2})?)(?:\/|$)/i)?.[1] || 'en';
    const urlLanguage = resolveSupportedLanguage(raw) || 'en';
    
    if (i18n.language !== urlLanguage) {
      i18n.changeLanguage(urlLanguage);
    }

    // 🧹 CLEANUP : Supprimer le cache-bust timestamp de l'URL après chargement
    if (window.location.search.includes('_t=') || window.location.search.includes('_reload=')) {
      const cleanUrl = window.location.pathname;
      window.history.replaceState({}, '', cleanUrl);
      logger.debug('Cache-bust cleaned, clean URL:', cleanUrl);
    }
  }, [ready, i18n]);

  // 🎯 ANALYTICS & TRACKING : après idle (INP) — langue déjà stable
  useEffect(() => {
    if (!ready || !i18n.isInitialized) return;

    const bootTracking = () => {
      if (typeof window === 'undefined') return;

      const trackedWindow = window as Window & { dataLayer?: Record<string, unknown>[] };
      trackedWindow.dataLayer = trackedWindow.dataLayer || [];
      trackedWindow.dataLayer.push({
        event: 'language_ready',
        language: i18n.language,
        page_path: window.location.pathname,
        page_title: document.title,
      });

      if (!import.meta.env.PROD) return;

      const gaId = import.meta.env.VITE_GA_MEASUREMENT_ID || import.meta.env.VITE_GA_ID;
      if (gaId) {
        import('./lib/ga').then(({ initGA }) => {
          initGA();
        });
      }

      import('./lib/clarity').then(({ initClarity }) => {
        initClarity();
      });
      import('./lib/tiktokPixel').then(({ initTikTokPixel }) => {
        initTikTokPixel();
      });
      // Meta / UET / LinkedIn already injected in index.html — skip SPA re-init (INP)
      import('./lib/tracking').then(({ initAllTracking }) => {
        initAllTracking();
      });
      import('./lib/gtm-helper').then((module) => {
        if (module.initAllTracking) {
          module.initAllTracking();
        }
      });
    };

    const w = window as Window & {
      requestIdleCallback?: (cb: () => void, opts?: { timeout: number }) => number;
      cancelIdleCallback?: (id: number) => void;
    };
    if (typeof w.requestIdleCallback === 'function') {
      const id = w.requestIdleCallback(bootTracking, { timeout: 4000 });
      return () => w.cancelIdleCallback?.(id);
    }
    const timer = window.setTimeout(bootTracking, 2500);
    return () => clearTimeout(timer);
  }, [ready, i18n]);

  // 🔍 INDICATEUR DE VERSION POUR VÉRIFIER LE DÉPLOIEMENT
  useEffect(() => {
    logger.debug('Komby Advanced Protection v2.0 loaded');
    
    // 📦 Initialiser le preloader des pages populaires
    PagePreloader.initPreloader();
  }, []);

  // 📦 Preload intelligent basé sur la page actuelle
  useEffect(() => {
    PagePreloader.preloadRelatedPages(currentPage);
  }, [currentPage]);

  // GA4 SPA pageviews (après navigation pushState / popstate)
  useEffect(() => {
    if (!import.meta.env.PROD) return;
    import('./lib/ga').then(({ trackGaPageView }) => {
      trackGaPageView();
    });
  }, [currentPage]);

  // 🔍 TRACKER DE NAVIGATION POUR DÉTECTER LA CORRUPTION
  useEffect(() => {
    let navigationCount = 0;
    
    const handlePopState = (event: PopStateEvent) => {
      navigationCount++;
      const currentPath = window.location.pathname;
      
      logger.debug(`Navigation #${navigationCount} - POPSTATE:`, { url: currentPath, state: event.state });
      
      if (currentPath.includes('pport') || currentPath.includes('susupport') || currentPath.includes('frog')) {
        logger.error('Corruption detected during navigation!', { navigationCount, historyLength: window.history.length });
      }
      
      const newPage = getPageFromPath(currentPath);
      setCurrentPage(newPage);
    };

    // Tracker des changements d'URL (même sans popstate)
    const originalPushState = window.history.pushState;
    const originalReplaceState = window.history.replaceState;
    
    window.history.pushState = function(_state: unknown, _title: string, url?: string | URL | null) {
      logger.debug('PUSHSTATE:', url);
      if (url && (url.toString().includes('pport') || url.toString().includes('frog'))) {
        logger.error('🚨 CORRUPTION DANS PUSHSTATE!', url);
      }
      const result = originalPushState.call(this, _state, _title, url);
      // 🔥 IMPORTANT: Mettre à jour la page après pushState (comme popstate)
      if (url) {
        const newPage = getPageFromPath(url.toString());
        logger.debug(`PushState → Page: ${newPage}`);
        setCurrentPage(newPage);
      }
      return result;
    };
    
    window.history.replaceState = function(_state: unknown, _title: string, url?: string | URL | null) {
      logger.debug('REPLACESTATE:', url);
      if (url && (url.toString().includes('pport') || url.toString().includes('frog'))) {
        logger.error('🚨 CORRUPTION DANS REPLACESTATE!', url);
      }
      return originalReplaceState.call(this, _state, _title, url);
    };

    window.addEventListener('popstate', handlePopState);
    
    return () => {
      window.removeEventListener('popstate', handlePopState);
      window.history.pushState = originalPushState;
      window.history.replaceState = originalReplaceState;
    };
  }, []);

  // 🛡️ PROTECTION GLOBALE WINDOW.OPEN
  useEffect(() => {
    const originalWindowOpen = window.open;
    window.open = function(url, target, features) {
      logger.debug('INTERCEPTION WINDOW.OPEN - Original URL:', url);
      
      if (url && typeof url === 'string' && (url.includes('pport') || url.includes('susupport') || url.includes('frog')) && !url.includes('/support')) {
        logger.error('🚨 CORRUPTION DÉTECTÉE DANS WINDOW.OPEN!', url);
        const correctedUrl = url.replace(/([a-z]{2})pport/g, '$1support').replace(/\/pport/g, '/support').replace(/susupport/g, 'support').replace(/\/frog/g, '/blog');
        logger.debug('Corrected URL:', correctedUrl);
        // alert(`CORRUPTION INTERCEPTÉE!\nOriginal: ${url}\nCorrigé: ${correctedUrl}`); // DÉSACTIVÉ POUR TEST
        console.groupEnd();
        return originalWindowOpen.call(this, correctedUrl, target, features);
      }
      
      logger.debug('Clean URL, opening normally');
      return originalWindowOpen.call(this, url, target, features);
    };

    return () => {
      window.open = originalWindowOpen;
    };
  }, []);

  // Protection complète contre la corruption d'URLs
  useEffect(() => {
    const currentPath = window.location.pathname;
    let correctedPath = currentPath;
    let hasCorrection = false;
    
    // 🔍 ANALYSE ET CORRECTION AUTOMATIQUE DE LA CORRUPTION (SKIP support pages)
    if ((currentPath.includes('pport') || currentPath.includes('susupport') || currentPath.includes('frog')) && !currentPath.includes('/support')) {
      logger.log('🚨 CORRUPTION URL DÉTECTÉE - ANALYSE COMPLÈTE');
      logger.error('Corrupted URL:', { path: currentPath, referrer: document.referrer });
      
      // Identifier la SOURCE de la corruption
      const possibleSources = [];
      if (document.referrer.includes('admin') || document.referrer.includes('_console')) {
        possibleSources.push('Navigation depuis admin');
      }
      if (window.history.length > 1) {
        possibleSources.push('Navigation historique');
      }
      if (sessionStorage.getItem('admin-context')) {
        possibleSources.push('Session admin active');
      }
      
      logger.debug('Possible sources:', possibleSources);
      
      // CORRECTION IMMÉDIATE sans alert (pour éviter l'interruption)
      const correctedPath = currentPath.replace(/([a-z]{2})pport/g, '$1support').replace(/\/pport/g, '/support').replace(/susupport/g, 'support').replace(/\/frog/g, '/blog');
      logger.warn('🔧 CORRECTION AUTOMATIQUE:', currentPath, '→', correctedPath);
      
      // Redirection silencieuse
      window.history.replaceState(null, '', correctedPath);
      return; // Arrêter l'exécution pour éviter la double correction
    }
    
    // Détecter et corriger différents types de corruption
    const corruptions = [
      { pattern: /frpport/g, replacement: 'support', name: 'frpport→support' },
      { pattern: /enpport/g, replacement: 'support', name: 'enpport→support' },
      { pattern: /depport/g, replacement: 'support', name: 'depport→support' },
      { pattern: /espport/g, replacement: 'support', name: 'espport→support' },
      { pattern: /itpport/g, replacement: 'support', name: 'itpport→support' },
      { pattern: /ptpport/g, replacement: 'support', name: 'ptpport→support' },
      { pattern: /nlpport/g, replacement: 'support', name: 'nlpport→support' },
      { pattern: /plpport/g, replacement: 'support', name: 'plpport→support' },
      { pattern: /rupport/g, replacement: 'support', name: 'rupport→support' },
      { pattern: /zhpport/g, replacement: 'support', name: 'zhpport→support' },
      { pattern: /japport/g, replacement: 'support', name: 'japport→support' },
      { pattern: /arpport/g, replacement: 'support', name: 'arpport→support' },
      { pattern: /kopport/g, replacement: 'support', name: 'kopport→support' },
      { pattern: /cspport/g, replacement: 'support', name: 'cspport→support' },
      { pattern: /svpport/g, replacement: 'support', name: 'svpport→support' },
      { pattern: /susupport/g, replacement: 'support', name: 'susupport→support' },
      { pattern: /\/pport\//g, replacement: '/support/', name: '/pport/→/support/' },
      { pattern: /\/pport$/g, replacement: '/support', name: '/pport$→/support' },
      { pattern: /\/frog\//g, replacement: '/blog/', name: '/frog/→/blog/' },
      { pattern: /\/frog$/g, replacement: '/blog', name: '/frog$→/blog' }
    ];
    
    corruptions.forEach(({ pattern, replacement, name }) => {
      if (pattern.test(correctedPath)) {
        correctedPath = correctedPath.replace(pattern, replacement);
        hasCorrection = true;
        console.warn(`🔧 Corruption URL détectée (${name}):`, currentPath, '→', correctedPath);
      }
    });
    
    if (hasCorrection) {
      // Redirection immédiate pour éviter l'affichage de la page corrompue
      window.location.replace(correctedPath);
      return; // Arrêter l'exécution
    }
  }, []);
  
  // Détection du pays utilisateur avec cache
  useEffect(() => {
    const detectCountry = async () => {
      try {
        // Vérifier cache géolocalisation (24h)
        const cacheKey = 'komby_geo_cache';
        const cached = localStorage.getItem(cacheKey);
        if (cached) {
          try {
            const { data, timestamp } = JSON.parse(cached);
            const isExpired = Date.now() - timestamp > 24 * 60 * 60 * 1000; // 24h
            if (!isExpired && data.country) {
              setDetectedCountry(data.country);
              return;
            }
          } catch {
            // Cache corrompu, continuer avec API
          }
        }

        // Utiliser l'API de géolocalisation IP avec timeout
        const controller = new AbortController();
        const timeout = setTimeout(() => controller.abort(), 3000);
        const response = await fetch('https://ipapi.co/json/', { 
          signal: controller.signal,
          mode: 'cors' as RequestMode 
        });
        clearTimeout(timeout);
        
        if (response.ok) {
          const data = await response.json();
          if (data.country_code) {
            setDetectedCountry(data.country_code);
            
            // Sauvegarder en cache (réutiliser le même cache que CookieBanner)
            try {
              const EU_COUNTRIES = [
                'AT', 'BE', 'BG', 'HR', 'CY', 'CZ', 'DK', 'EE', 'FI', 'FR', 'DE', 'GR', 
                'HU', 'IE', 'IT', 'LV', 'LT', 'LU', 'MT', 'NL', 'PL', 'PT', 'RO', 'SK', 
                'SI', 'ES', 'SE', 'IS', 'LI', 'NO', 'CH'
              ];
              const country = data.country_code.toUpperCase();
              const isEU = EU_COUNTRIES.includes(country);
              localStorage.setItem(cacheKey, JSON.stringify({
                data: { country, isEU },
                timestamp: Date.now()
              }));
            } catch {
              // Ignore cache errors
            }
          }
        }
      } catch (error) {
        logger.debug('Geolocation not available:', error);
      }
    };

    detectCountry();
  }, []);

  // 🎬 Stable navigation function using useCallback
  const navigateToPage = useCallback(async (page: string) => {
    logger.debug('Navigation to:', page);
    
    // Éviter la navigation si on est déjà sur la page
    if (currentPage === page) {
      logger.debug('Already on page', page);
      return;
    }
    
    // Détecter la langue actuelle depuis l'URL
    const currentPath = window.location.pathname;
    const langMatch = currentPath.match(/^\/([a-zA-Z]{2}(?:-[A-Z]{2})?)(?:\/|$)/i);
    const currentLang = langMatch ? langMatch[1] : '';
    
    // 🎯 Détecter le skin actuel depuis l'URL ou localStorage
    const skinFromUrl = currentPath.match(/\/(resto|office|nightclub)(?:\/|$)/i)?.[1]?.toLowerCase();
    const skinFromStorage = localStorage.getItem('komby-theme');
    const currentSkin = skinFromUrl || skinFromStorage || 'office';
    
    const pathMap: Record<string, string> = {
      'home': '/',
      'admin': '/_console',
      'admin-ticket-detail': '/_console/ticket/',
      'pricing': `/${currentSkin}/pricing/`, // 🎯 URL par skin avec trailing slash
      'contact': '/contact/',
      'compare': '/compare/',
      'compare-landing': '/compare/',
      'use-case': '/use-case/',
      'replace-landing': '/replace/',
      'about': '/about/',
      'blog': '/blog/',
      'blog-post': '/blog/', // Blog posts use dynamic routing
      'press': '/press/',
      'team': '/team/',
      'music': '/music/',
      'careers': '/careers/',
      'ai-policy': '/ai-policy/',
      'help': '/help/',
      'support-request': '/request/',
      'success': '/success/',
      'cgv': '/cgv/',
      'rgpd': '/rgpd/',
      'checkout': '/checkout/',
      'reset-password': '/auth/reset-password/',
    };
    
    let newPath = pathMap[page] || '/';
    
    // Si on est sur une page localisée, préserver la langue
    if (currentLang && newPath !== '/') {
      newPath = `/${currentLang}${newPath}`;
    } else if (currentLang && newPath === '/') {
      newPath = `/${currentLang}`;
    }
    
    if (window.location.pathname !== newPath) {
      // Simple navigation - update state and URL
      logger.debug('Navigating to:', newPath);
      setCurrentPage(page);
      window.history.pushState({ page }, '', newPath);
      logger.debug('Navigation completed to:', page);
    }
  }, [currentPage]);

  useEffect(() => {
    const onOnyxNavigate = (event: Event) => {
      const page = (event as CustomEvent<{ page?: string }>).detail?.page;
      if (typeof page === 'string' && page.trim()) {
        void navigateToPage(page.trim());
        window.scrollTo({ top: 0, behavior: 'smooth' });
      }
    };
    window.addEventListener(ONYX_NAVIGATE_EVENT, onOnyxNavigate);
    return () => window.removeEventListener(ONYX_NAVIGATE_EVENT, onOnyxNavigate);
  }, [navigateToPage]);

  // 🎬 FONCTION POUR RENDRE LE CONTENU D'UNE PAGE - memoized to prevent recreating on each render
  const pageContent = useMemo(() => {
    const currentPath = window.location.pathname;
    
    switch (currentPage) {
      case 'admin': 
        return (
          <AdminProtectedRoute>
            <UnifiedAdminDashboard />
          </AdminProtectedRoute>
        );
      case 'admin-ticket-detail': {
        // Extract ticketId from URL: /_console/ticket/:ticketId
        const ticketIdMatch = currentPath.match(/\/_console\/ticket\/([^/]+)/);
        const ticketId = ticketIdMatch ? ticketIdMatch[1] : undefined;
        return (
          <AdminProtectedRoute>
            <TicketDetailView ticketId={ticketId} />
          </AdminProtectedRoute>
        );
      }
      case 'pricing': return <Pricing />;
      case 'contact': return <Contact />;
      case 'compare': return <Compare />;
      case 'compare-landing': {
        const compareSlug = currentPath.match(/\/compare\/([^/]+)/)?.[1];
        return <CompareLanding slug={compareSlug} />;
      }
      case 'use-case': {
        const useCaseSlug = currentPath.match(/\/use-case\/([^/]+)/)?.[1];
        return <UseCaseLanding slug={useCaseSlug} />;
      }
      case 'replace-landing': {
        const replaceSlug = currentPath.match(/\/replace\/([^/]+)/)?.[1];
        return <ReplaceLanding slug={replaceSlug} />;
      }
      case 'about': return <About />;
      case 'blog': return <Blog />;
      case 'blog-post': {
        // Extract blog slug from URL: /{lang}/blog/{slug}
        const blogSlugMatch = currentPath.match(/\/blog\/([^/]+)/);
        const blogSlug = blogSlugMatch ? blogSlugMatch[1] : undefined;
        return <BlogPost slug={blogSlug} />;
      }
      case 'press': return <Press />;
      case 'team': return <Team />;
      case 'music': return <Music />;
      case 'careers': return <Careers />;
      case 'ai-policy': return <AIPolicyPage />;
      case 'help': 
        return <SupportCenter onNavigateToForm={() => navigateToPage('support-request')} />;
      case 'support-request':
        return <SupportTicketForm />;
      case 'success': return <Success />;
      case 'cgv': return <CGV />;
      case 'rgpd': return <RGPD />;
      case 'checkout': {
        // Extract plan and billing from URL params
        const urlParams = new URLSearchParams(window.location.search);
        const planId = urlParams.get('plan') || 'pro';
        const billing = (urlParams.get('billing') as 'monthly' | 'yearly') || 'monthly';
        return <CheckoutPage planId={planId} billing={billing} />;
      }
      case 'reset-password':
        return <ResetPassword />;

      default: return <Home />;
    }
  }, [currentPage, navigateToPage]);

  const handleLanguageSwitch = useCallback((language: string) => {
    logger.debug('Language switch:', {
      from: i18n.language,
      to: language,
      currentPath: window.location.pathname,
      currentPage
    });
    
    // Changer la langue dans i18next
    i18n.changeLanguage(language);
    
    // Construire la nouvelle URL en gardant la même page
    const currentPath = window.location.pathname;
    // Vérifier si le path commence vraiment par un code de langue VALIDE
    // SANCTIONS: 'ru' and 'fa' temporarily hidden - Russia & Iran
    const validLanguages = ['en', 'fr', 'es', 'de', 'it', 'pt', 'nl', 'pl', 'zh', 'ja', 'ar', 'ko', 'cs', 'sv', 'no', 'da', 'fi', 'sk', 'sl', 'hr', 'bg', 'ro', 'th', 'vi', 'id', 'ms', 'hi', 'el', 'hu', 'lv', 'lt', 'et', 'mt', 'ga', 'cy', 'is', 'mk', 'sq', 'bs', 'sr', 'me', 'tr', 'ka', 'hy', 'az', 'kk', 'ky', 'uz', 'tk', 'mn', 'en-US', 'en-CA', 'en-AU', 'en-GB', 'en-SG', 'en-ZA', 'en-NZ', 'pt-BR', 'fr-CH', 'es-MX', 'es-ES', 'it-CH', 'de-CH'];
    const langMatch = currentPath.match(/^\/([a-zA-Z]{2}(?:-[A-Z]{2})?)(?:\/|$)/i);
    const detectedLang = langMatch ? langMatch[1] : null;
    
    // Vérifier que la langue détectée est dans notre liste de langues valides
    const isValidLanguage = detectedLang && validLanguages.includes(detectedLang);
    const pathWithoutLang = (langMatch && isValidLanguage) ? currentPath.replace(/^\/[a-zA-Z]{2}(?:-[A-Z]{2})?/i, '') || '/' : currentPath;
    const newPath = `/${language}${pathWithoutLang === '/' ? '' : pathWithoutLang}`;
    
    logger.debug('Internal navigation (staying on same page):', {
      currentPath,
      pathWithoutLang,
      newPath,
      currentPage
    });
    
    // Utiliser pushState pour changer l'URL sans recharger la page (reste sur la même page)
    window.history.pushState({}, '', newPath);
    
    // Déclencher manuellement l'événement popstate pour que le router réagisse
    window.dispatchEvent(new PopStateEvent('popstate'));
  }, [i18n, currentPage]);
  
  // 🎯 CRITIQUE : Attendre que i18next soit prêt AVANT de rendre le contenu
  // Cela empêche Googlebot de voir le fallback anglais pendant les 100ms de chargement
  if (!ready || !i18n.isInitialized) {
    return (
      <div style={{ 
        minHeight: '100vh', 
        display: 'flex', 
        alignItems: 'center', 
        justifyContent: 'center'
      }}>
        <div style={{ textAlign: 'center' }}>
          <div style={{ 
            width: '40px', 
            height: '40px', 
            border: '3px solid #f3f4f6', 
            borderTopColor: '#F4D517', 
            borderRadius: '50%', 
            animation: 'spin 1s linear infinite',
            margin: '0 auto 1rem'
          }}></div>
          <p style={{ color: '#6b7280', fontSize: '14px' }}>Loading Komby...</p>
        </div>
        <style>{`
          @keyframes spin {
            to { transform: rotate(360deg); }
          }
        `}</style>
      </div>
    );
  }
  
  logger.debug('i18next ready, rendering app with language:', i18n.language);

  const isAdminConsole =
    currentPage === 'admin' || currentPage === 'admin-ticket-detail';

  return (
    <div className={`app-root relative isolate min-h-screen transition-colors duration-300 ${surface === 'black' ? 'black-root' : 'bg-gradient-to-br from-blue-50 to-indigo-100 dark:from-gray-950 dark:via-gray-950 dark:to-gray-900'}`}>
      {surface === 'black' && particlesReady && (
        <>
          <div className="radial-layer" />
          <div className="pointer-events-none fixed inset-0 z-0">
            <Suspense fallback={null}>
              <LazyParticles />
            </Suspense>
          </div>
        </>
      )}
      {!isAdminConsole && (
        <LanguageSuggestionBanner 
          detectedCountry={detectedCountry}
          onLanguageSwitch={handleLanguageSwitch}
          currentPage={currentPage}
        />
      )}
      <HreflangTags />
      <BreadcrumbsJSONLD />
      <DynamicOpenGraph page={currentPage} />
      
      <ThemeSelectorModal />
      {!isAdminConsole && (
        <Header 
          isMenuOpen={isMenuOpen} 
          setIsMenuOpen={setIsMenuOpen} 
          currentPage={currentPage}
          setCurrentPage={navigateToPage}
        />
      )}
      
      <main className={`relative z-10 min-h-screen ${isAdminConsole ? '' : ''}`}>
        {/* 🎬 SYSTÈME SIMPLIFIÉ - Direct rendering sans Netflix transitions */}
        <Suspense fallback={<PageFallback />}>
          {pageContent}
        </Suspense>
        {!isAdminConsole && <SeoVerticalBody />}
      </main>
      
      {!isAdminConsole && <Footer onNavigate={navigateToPage} />}
      {!isAdminConsole && <CookieBanner />}
      {!isAdminConsole && <HelpChatFabGate />}
    </div>
  );
}

function App() {
  return (
    <NotificationProvider>
      <AuthProvider>
        <ColorSchemeProvider>
          <ThemeProvider>
            <CurrencyProvider>
              <AIHubProvider>
                <EmailHubProvider>
                  <AppContent />
                </EmailHubProvider>
              </AIHubProvider>
            </CurrencyProvider>
          </ThemeProvider>
        </ColorSchemeProvider>
      </AuthProvider>
    </NotificationProvider>
  );
}

export default App;
